import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import RenderStructuredData, {
  humanizeStructuredDataKey,
  isEmptyStructuredDataValue,
} from 'components/DetailTable/RenderStructuredData';
import {
  StructuredDataObject,
  StructuredDataValue,
} from 'components/DetailTable/types';
import { showFailureToast, showInfoToast } from 'lib/ToastHelper';
import validateFieldForPlaceholder from 'lib/validateFieldPlaceholder';
import React from 'react';
import ReactMarkdown from 'react-markdown';

import styles from './BPAFindingRemediationTab.module.css';

interface Props {
  remediation?: StructuredDataObject | null;
}

interface CodeBlockProps {
  code: string;
  language?: string;
  copyLabel?: string;
  copySuccessMessage?: string;
}

interface RemediationEntry {
  key: string;
  label: string;
  value: StructuredDataValue;
  language?: string;
  isCode: boolean;
  isPlainCliCommand: boolean;
}

const LEADING_CODE_FENCE = /^\s*(?:```|~~~)([^\s\r\n`]*)/;
const SAFE_LINK = /^https?:\/\//i;
const REMEDIATION_LABEL_OVERRIDES: Record<string, string> = {
  other: 'Resolution Steps',
};

const getLeadingCodeLanguage = (
  value: StructuredDataValue,
): string | undefined => {
  if (typeof value !== 'string') return undefined;

  const match = value.match(LEADING_CODE_FENCE);
  return match ? match[1] || 'code' : undefined;
};

const getValueBadge = (entry: RemediationEntry): string => {
  if (entry.language) return entry.language.toUpperCase();
  if (Array.isArray(entry.value)) return 'List';
  if (entry.value !== null && typeof entry.value === 'object') return 'Details';
  return 'Instructions';
};

const CodeBlock: React.FC<CodeBlockProps> = ({
  code,
  language,
  copyLabel = 'Copy',
  copySuccessMessage = 'Code copied to clipboard',
}) => {
  const languageLabel = language ? language.toUpperCase() : 'CODE';

  const copyCode = async () => {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard API is not available.');
      }

      await navigator.clipboard.writeText(code);
      showInfoToast(copySuccessMessage);
    } catch (_error) {
      showFailureToast('Unable to copy code to the clipboard.');
    }
  };

  return (
    <div className={styles.codeBlock}>
      <div className={styles.codeHeader}>
        <span>{languageLabel}</span>
        <button
          type="button"
          className={styles.copyButton}
          aria-label={`Copy ${languageLabel} code to clipboard`}
          title="Copy code"
          onClick={() => void copyCode()}
        >
          <ContentCopyOutlinedIcon aria-hidden="true" />
          <span>{copyLabel}</span>
        </button>
      </div>
      <pre className={styles.codeContent}>
        <code>{code}</code>
      </pre>
    </div>
  );
};

const RemediationMarkdown: React.FC<{ children: string }> = ({ children }) => (
  <ReactMarkdown
    className={styles.markdown}
    components={{
      a: ({ node: _node, href, children: linkText, ...props }) =>
        href && SAFE_LINK.test(href) ? (
          <a {...props} href={href} target="_blank" rel="noopener noreferrer">
            {linkText}
          </a>
        ) : (
          <span>{linkText}</span>
        ),
      code({ node: _node, inline, className, children: code, ...props }) {
        if (inline) {
          return (
            <code className={styles.inlineCode} {...props}>
              {code}
            </code>
          );
        }

        const language = /language-([\w-]+)/.exec(className || '')?.[1];
        return (
          <CodeBlock
            code={String(code).replace(/\n$/, '')}
            language={language}
          />
        );
      },
      img: ({ node: _node, alt }) => (
        <span className={styles.imagePlaceholder}>
          [Image omitted{alt ? `: ${alt}` : ''}]
        </span>
      ),
      pre: ({ node: _node, children: code }) => <>{code}</>,
    }}
  >
    {children}
  </ReactMarkdown>
);

const BPAFindingRemediationTab: React.FC<Props> = ({ remediation }) => {
  if (!remediation || Object.keys(remediation).length === 0) {
    return (
      <div className={styles.emptyState}>
        {validateFieldForPlaceholder(null)}
      </div>
    );
  }

  const entries: RemediationEntry[] = Object.entries(remediation)
    .map(([key, value]) => {
      const language = getLeadingCodeLanguage(value);
      const isPlainCliCommand =
        key.trim().toLowerCase() === 'cli' &&
        typeof value === 'string' &&
        value.trim() !== '' &&
        language === undefined;

      return {
        key,
        label:
          REMEDIATION_LABEL_OVERRIDES[key.trim().toLowerCase()] ||
          humanizeStructuredDataKey(key),
        value,
        language: isPlainCliCommand ? 'cli' : language,
        isCode: language !== undefined || isPlainCliCommand,
        isPlainCliCommand,
      };
    })
    .sort(
      (left, right) =>
        Number(left.isCode) - Number(right.isCode) ||
        left.label.localeCompare(right.label),
    );

  const firstContentIndex = entries.findIndex(
    (entry) => !isEmptyStructuredDataValue(entry.value),
  );
  const defaultOpenIndex = Math.max(firstContentIndex, 0);

  return (
    <section className={styles.remediation} aria-labelledby="remediation-title">
      <header className={styles.heading}>
        <h3 id="remediation-title">Remediation Instructions</h3>
        <p>Review the available guidance and implementation examples.</p>
      </header>

      <div className={styles.sections}>
        {entries.map((entry, index) => (
          <details
            className={styles.section}
            key={entry.key}
            open={index === defaultOpenIndex}
          >
            <summary className={styles.sectionSummary}>
              <span className={styles.sectionTitle}>{entry.label}</span>
              <span className={styles.badge}>{getValueBadge(entry)}</span>
            </summary>
            <div className={styles.sectionContent}>
              {entry.isPlainCliCommand && typeof entry.value === 'string' ? (
                <>
                  <CodeBlock
                    code={entry.value}
                    language="cli"
                    copyLabel={entry.value.includes('\n') ? 'Copy all' : 'Copy'}
                    copySuccessMessage="CLI command copied to clipboard"
                  />
                  <p className={styles.commandNote}>
                    Review the command and replace any placeholders before
                    running it.
                  </p>
                </>
              ) : (
                <RenderStructuredData
                  value={entry.value}
                  renderString={(value) => (
                    <RemediationMarkdown>{value}</RemediationMarkdown>
                  )}
                />
              )}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
};

export default BPAFindingRemediationTab;
